import {
  Component,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TNil } from '@app/app.interface';
import { IIconIdForm } from '@campaign-form/campaign-form.interface';
import { AuthService } from '@core/services/auth.service';
import { ProfileDateFilterStateService } from '@core/services/profile-date-filter-state.service';
import { TranslateModule } from '@ngx-translate/core';
import { CampaignsDatatableComponent } from '@pages/campaigns/campaigns-datatable/campaigns-datatable.component';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { CampaignsStore } from '@pages/campaigns/campaigns.store';
import { ICustomer } from '@pages/customers/customers.interface';
import { CustomersStore } from '@pages/customers/customers.store';
import { BaseComponent } from '@shared/components/base.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import {
  EDateRangeType,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { CustomerDetailsBannerComponent } from './customer-details-banner/customer-details-banner.component';
import { CustomerDetailsFiltersComponent } from './customer-details-filters/customer-details-filters.component';
import { ICustomerDetailsFilter } from './customer-details.interface';

@Component({
  imports: [
    RouterModule,
    MatButtonModule,
    MatMenuModule,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ContentContainerComponent,
    MatTabsModule,
    CustomerDetailsBannerComponent,
    CustomerDetailsFiltersComponent,
    forwardRef(() => CampaignsDatatableComponent),
  ],
  providers: [CustomersStore, CampaignsStore],
  selector: 'app-customer-details',
  styleUrls: ['./customer-details.component.scss'],
  templateUrl: './customer-details.component.html',
})
export class CustomerDetailsComponent extends BaseComponent implements OnInit {
  public readonly customerUpdated = output<ICustomer>();

  public readonly customerId = signal<string | TNil>(null);
  public readonly dateFilterState = signal<IProfileDateFilterState | null>(null);
  public readonly customer = signal<ICustomer | null>(null);
  public readonly campaigns = signal<TCampaignApi[]>([]);
  public readonly isCreatingCustomer = signal<boolean>(false);
  public readonly nameError = signal<string | null>(null);
  public readonly hasUnfilteredCampaigns = signal<boolean>(false);

  public readonly customerDetailsFilter = signal<ICustomerDetailsFilter>({
    endDate: null,
    startDate: null,
  });

  public readonly nameForm: FormGroup<{ name: FormControl<string | null> }> = new FormGroup({
    name: new FormControl<string | null>(null, Validators.required),
  });

  public readonly iconForm: FormGroup<IIconIdForm> = new FormGroup({
    iconId: new FormControl<string | null>(null),
    imageFile: new FormControl<ICustomFile | null>(null, imageExtensionValidator()),
  });

  public readonly loadedCustomerData = signal(false);
  public readonly loadedCampaignsData = signal(false);
  public readonly hideFilters = signal(true);

  public isEditingName = false;

  public readonly campaignsStore = inject(CampaignsStore);
  private readonly customersStore = inject(CustomersStore);
  private readonly profileDateFilterStateService = inject(ProfileDateFilterStateService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private selectedDateRange = EDateRangeType.last30Days;

  private readonly bannerComponent = viewChild(CustomerDetailsBannerComponent);

  constructor() {
    super();

    this.setCustomerId();
    this.initializeDateFilterState();
    this.setInitialFilters();
    this.setInitialCustomer();
    this.nameFormSubscriptions();
    this.createEffects();
  }

  // This is the only place that fetches the customer on load - there used to be a second,
  // duplicate fetch in an effect keyed off customerId(), which raced this one: if the extra GET
  // resolved after a quick rename, it overwrote the just-saved name with stale pre-rename data
  // until the next reload.
  public ngOnInit(): void {
    const customerId = this.route.snapshot.paramMap.get('id');

    if (customerId) {
      this.customersStore.fetchCustomer(customerId);
    }

    this.fetchCampaigns();
  }

  public onBannerNameEditingChanged(isEditing: boolean): void {
    this.isEditingName = isEditing;
    // Clears out a stale error from a previous attempt, whether we're opening the form fresh or
    // the banner just discarded (both go through here) - otherwise a rejected name from an earlier
    // edit would still show red the next time the field opens.
    this.nameError.set(null);
    this.nameForm.get('name')?.setErrors(null);
  }

  public onBannerNameSaveRequested(): void {
    if (this.customer() && this.nameForm.valid && this.isEditingName) {
      this.saveName();
    }
  }

  public onCustomerDetailsFilterChanged(customerDetailsFilter: ICustomerDetailsFilter): void {
    this.customerDetailsFilter.set(customerDetailsFilter);
  }

  public onDateRangeChanged(dateRange: Pick<ICustomerDetailsFilter, 'endDate' | 'startDate'>): void {
    this.customerDetailsFilter.set({
      ...this.customerDetailsFilter(),
      endDate: dateRange.endDate,
      startDate: dateRange.startDate,
    });

    this.profileDateFilterStateService.persistState({
      endDate: dateRange.endDate,
      entityId: (this.route.snapshot.paramMap.get('id') || this.customerId()) ?? null,
      entityType: 'customer',
      selectedRange: this.selectedDateRange,
      startDate: dateRange.startDate,
      userId: AuthService.currentDbUser?.id ?? null,
    });
  }

  public onSelectedRangeChanged(selectedRange: EDateRangeType): void {
    this.selectedDateRange = selectedRange;
  }

  private setInitialCustomer(): void {
    effect(() => {
      const customer = this.customersStore.customer$();
      const routeCustomerId = this.route.snapshot.paramMap.get('id');

      if (customer && routeCustomerId && !untracked(this.customer)) {
        this.setCustomer(customer);
      }
    });
  }

  private nameFormSubscriptions(): void {
    this.nameForm
      .get('name')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => {
        if (this.nameError()) {
          this.nameError.set(null);
          this.nameForm.get('name')?.setErrors(null);
        }

        if (this.isEditingName && (!name || name.trim() === '')) {
          this.nameForm.patchValue({ name: '' }, { emitEvent: false });

          return;
        }
      });
  }

  private createEffects(): void {
    this.updatedCustomerEffect();
    this.campaignsEffect();
    this.loadedCustomerDataEffect();
  }

  private updatedCustomerEffect(): void {
    effect(() => {
      const updatedCustomer = this.customersStore.updatedCustomer$();

      if (!updatedCustomer) {
        return;
      }

      this.setCustomer(
        { ...this.customer(), iconId: updatedCustomer.iconId, id: updatedCustomer.id, name: updatedCustomer.name },
        false,
      );
      this.customerUpdated.emit(updatedCustomer);
      this.customersStore.updatedCustomer$.set(null);
    });
  }

  private campaignsEffect(): void {
    effect(() => {
      // Reads from the dedicated fetchProfileCampaigns pipeline (see campaigns.store.ts), not
      // campaignsStore.campaigns$() - that field is driven by CampaignsDatatableComponent's own
      // page/sort/filter-triggered fetches for the Campaigns tab's table, and sharing it with this
      // tab's own full-list fetch used to mean whichever response landed last silently overwrote
      // the other's data and cancelled its in-flight request.
      const campaigns = this.campaignsStore.profileCampaigns$();

      if (!this.hasUnfilteredCampaigns() && campaigns?.length > 0) {
        this.hasUnfilteredCampaigns.set(true);
      }

      this.campaigns.set(campaigns);
      this.loadedCampaignsData.set(!this.campaignsStore.profileCampaignsLoading$());
    });
  }

  private loadedCustomerDataEffect(): void {
    effect(() => {
      // campaignsStore.loading$() now only covers CampaignsDatatableComponent's own paginated
      // fetches (see fetchProfileCampaigns in campaigns.store.ts) - this page's own campaigns()
      // signal is driven by profileCampaignsLoading$() instead, so that's what belongs here.
      const loading = this.customersStore.loading$() || this.campaignsStore.profileCampaignsLoading$();
      this.loadedCustomerData.set(!loading);
    });
  }

  private fetchCampaigns(): void {
    const customerId = this.route.snapshot.paramMap.get('id') || this.customerId();

    if (customerId) {
      this.campaignsStore.fetchProfileCampaigns({ ctaTemplates: true, customerId });
    }
  }

  private saveName(): void {
    if (!this.nameForm.valid || this.isCreatingCustomer()) {
      return;
    }

    this.updateName(this.customer()!, this.nameForm.value.name!.trim());
  }

  private updateName(customer: ICustomer, newName: string): void {
    this.isCreatingCustomer.set(true);
    this.nameError.set(null);
    this.nameForm.get('name')?.setErrors(null);
    this.nameForm.disable();

    this.customersStore
      .updateCustomerName(customer.id, { iconId: customer.iconId, name: newName })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.isCreatingCustomer.set(false);
          this.nameForm.enable();

          const nameError = this.customersStore.nameError$();

          if (nameError) {
            this.nameError.set(nameError);
            this.nameForm.get('name')?.markAsTouched();
            this.nameForm.get('name')?.setErrors({ error: true });
          }
        },
        next: (updatedCustomer) => {
          this.isCreatingCustomer.set(false);
          this.nameForm.enable();
          this.isEditingName = false;

          const confirmedCustomer = { ...updatedCustomer, name: newName };
          this.setCustomer(confirmedCustomer);
          this.customerUpdated.emit(confirmedCustomer);
        },
      });
  }

  public updateIcon(): void {
    const customer = this.customer();

    if (!customer) {
      return;
    }

    customer.iconId = this.iconForm.value.iconId;

    this.customersStore.updateCustomer({ customer, id: customer.id });
  }

  private setCustomer(customer: ICustomer | null, emitEvent = true): void {
    this.customer.set(customer);

    if (customer) {
      this.iconForm.patchValue(
        {
          iconId: customer.iconId,
          imageFile: ImageUtilitiesService.isValidFileId(customer.iconId)
            ? FileUploaderService.createCustomFile(customer.iconId!)
            : null,
        },
        { emitEvent },
      );
    }

    const banner = this.bannerComponent();

    if (banner) {
      banner.setCustomer(customer, emitEvent);
    }
  }

  private setInitialFilters(): void {
    const persistedDateFilterState = this.dateFilterState();

    if (persistedDateFilterState?.startDate && persistedDateFilterState.endDate) {
      this.customerDetailsFilter.set({
        endDate: endOfDay(persistedDateFilterState.endDate),
        startDate: startOfDay(persistedDateFilterState.startDate),
      });

      return;
    }

    const today = endOfDay(new Date());
    const last30DaysStart = startOfDay(subDays(today, 29));

    this.customerDetailsFilter.set({
      endDate: today,
      startDate: last30DaysStart,
    });
  }

  private initializeDateFilterState(): void {
    const customerId = (this.route.snapshot.paramMap.get('id') || this.customerId()) ?? null;
    const today = endOfDay(new Date());
    const last30DaysStart = startOfDay(subDays(today, 29));
    const resolvedState = this.profileDateFilterStateService.resolveState({
      defaultEndDate: today,
      defaultSelectedRange: EDateRangeType.last30Days,
      defaultStartDate: last30DaysStart,
      entityId: customerId,
      entityType: 'customer',
      hideAllTimeRangeOption: true,
      userId: AuthService.currentDbUser?.id ?? null,
    });
    const resolvedStateUnknown: unknown = resolvedState;

    if (!this.isResolvedDateFilterState(resolvedStateUnknown)) {
      return;
    }

    this.dateFilterState.set(resolvedStateUnknown.filterState);
    this.selectedDateRange = resolvedStateUnknown.selectedRange;
  }

  private isResolvedDateFilterState(
    value: unknown,
  ): value is { filterState: IProfileDateFilterState | null; selectedRange: EDateRangeType } {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return 'filterState' in value && 'selectedRange' in value;
  }

  private setCustomerId(): void {
    const urlCustomerId = this.route.snapshot.paramMap.get('id');

    this.customerId.set(urlCustomerId || this.customer()?.id);
  }
}
